import React, { useState, useEffect } from 'react';

import Td from 'components/Td';
import { Proposal } from 'generated/graphql';
import { ISimulationResult } from 'interfaces/interfaces';
import { formatMoney } from 'utils/utils';
import { getProductBrand, brandArticle } from 'constants/brand-constants';
import Modal from './commons/modal';

interface IPaymentSummaryProps {
    proposal: Proposal;
    simulation: ISimulationResult;
    insurance: boolean;
    productBizId?: string | null;
}

const PaymentSummary: React.FC<IPaymentSummaryProps> = (props): JSX.Element => {
    const { proposal, simulation, insurance, productBizId } = props;

    const brand = getProductBrand(productBizId);
    const { na_no } = brandArticle(brand);

    const [isDesktop, setDesktop] = useState(false);
    const [openModal, setOpenModal] = useState(false);

    useEffect(() => {
        setDesktop(window.innerWidth > 767);
    }, [window.innerWidth]);

    return (
        <>
            <div className="grid-summary highlight-card">
                <div className="pt-4 px-4 grid-summary-body">
                    <h3 className="mb-4 mt-3">
                        seu empréstimo {na_no} {brand.branchShortName}
                    </h3>
                    <table className="table table-borderless table-sm">
                        <tbody>
                            <tr className="font-weight-bolder">
                                <td>
                                    Empréstimo
                                </td>
                                <Td value={proposal.amount} />
                            </tr>
                            {
                                insurance &&
                                <tr>
                                    <td>
                                        Seguro
                                    </td>
                                    <Td value={simulation.insurancePremium} />
                                </tr>
                            }
                            <tr>
                                <td>
                                    Juros
                                </td>
                                <Td value={simulation.interestAmount} />
                            </tr>
                            <tr>
                                <td>
                                    IOF
                                </td>
                                <Td value={simulation.iof} />
                            </tr>
                            <tr>
                                <td>
                                    Tarifa de cadastro
                                </td>
                                <td>
                                    {/* {acceptedProposal.fixedComission ?
                                    <Fragment>
                                        <div className="outdated-value">
                                            R$ {formatMoney(simulation.originalComission)}
                                        </div>
                                        <div className="current-value">
                                            R$ {formatMoney(simulation.finalComission)}
                                        </div>
                                    </Fragment>
                                    : */}
                                    <div>
                                        R$ {formatMoney(simulation.finalComission)}
                                    </div>
                                    {/* } */}
                                </td>
                            </tr>
                            {simulation.guaranteeConcessionAmount && (
                                <tr>
                                    <td>
                                        CCA
                                        <div className='cca-information' onClick={() => setOpenModal(true)}>O que é CCA?</div>
                                    </td>
                                    <td>

                                        <div>
                                            R$ {formatMoney(simulation.guaranteeConcessionAmount)}
                                        </div>
                                    </td>
                                </tr>
                            )}
                            <tr className="total-line">
                                <td>
                                    Total a pagar
                                </td>
                                <Td value={simulation.total} />
                            </tr>
                        </tbody>
                    </table>
                    {!isDesktop &&
                        <p className="mobile-only">
                            O Custo Efetivo Total (CET) é de {(simulation.cet.monthlyRate * 100).toFixed(2)}% ao mês ou {(simulation.cet.annualyRate * 100).toFixed(2)}%
                            ao ano e já está incluído no valor total a pagar
                        </p>
                    }
                </div>
                <div className="grid-summary-footer border-top mt-3 px-4 pt-4 pb-2">
                    {proposal.downPayments != 0 &&
                        <div>
                            {isDesktop &&
                                <h4 className="desktop-only">
                                    Primeiras <span className="font-weight-bold">{proposal.downPayments} parcelas</span> reduzidas de
                                </h4>
                            }
                            {!isDesktop &&
                                <h4 className="mobile-only">
                                    <span className="font-weight-bold">{proposal.downPayments} parcelas</span> reduzidas de
                                </h4>
                            }
                            <h2 className="primary-text summary-amount-value">
                                {formatMoney(simulation.downPaymentPmt)}
                            </h2>
                            <p className="payments-annotation">
                                Parcelas referentes apenas aos juros.
                            </p>
                        </div>
                    }
                    <div>
                        {isDesktop &&
                            <h4 className="desktop-only">
                                {proposal.downPayments != 0 ? <>após isso,</> : <>Pagamento em</>} <span className="font-weight-bold">{proposal.tenure} parcelas</span> de
                            </h4>
                        }
                        {!isDesktop &&
                            <h4 className="mobile-only">
                                <span className="font-weight-bold">{proposal.tenure}</span> parcelas de
                            </h4>
                        }
                        <h2 className="primary-text summary-amount-value">
                            {formatMoney(simulation.pmt)}
                        </h2>
                        <p className="payments-annotation">
                            Parcelas referentes ao valor solicitado e taxas.
                        </p>
                    </div>
                    {
                        props.children
                    }
                    {isDesktop &&
                        <p className="desktop-only">
                            O Custo Efetivo Total (CET) é de {(simulation.cet.monthlyRate * 100).toFixed(2)}% ao mês ou {(simulation.cet.annualyRate * 100).toFixed(2)}%
                            ao ano e já está incluído no valor total a pagar
                        </p>
                    }
                </div>
            </div>
            <Modal
                title="O que é a CCA?"
                visible={openModal}
                onClose={() => setOpenModal(false)}
            >
                <p>
                    A Comissão de Concessão de Aval (CCA) é uma tarifa cobrada para viabilizar o seu empréstimo. Ela remunera a garantia utilizada na
                    operação, chamada aval, que reduz o risco da concessão e permite
                    que o crédito seja liberado. Essa comissão não é um custo extra
                    escondido, pois faz parte do valor total do empréstimo e aparece
                    detalhada no contrato de forma transparente.</p>
                <p>
                    Este empréstimo conta com garantia do Sebrae, que cobre parte do
                    valor em caso de inadimplência. A taxa de concessão de aval (CCA)
                    é paga uma única vez no momento da contratação.
                </p>
            </Modal>
        </>
    );
};

export default PaymentSummary;
